import React, { useState, useEffect, Fragment } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add,
  Delete,
  CheckCircle,
  Cancel,
  Email,
  Person,
} from '@mui/icons-material';
import { repAPI } from '../../services/api';
import LeadLoginRequestDialog from './LeadLoginRequestDialog';

const LeadLoginRequests = ({ lead, onRequestUpdated, isRep = true }) => {
  const [loginRequests, setLoginRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);

  useEffect(() => {
    if (lead?.loginRequests) {
      setLoginRequests(lead.loginRequests);
      setLoading(false);
    } else {
      fetchLoginRequests();
    }
  }, [lead]);

  const fetchLoginRequests = async () => {
    try {
      setLoading(true);
      const response = await repAPI.getLeadLoginRequests(lead.id);
      if (response.success) {
        setLoginRequests(response.data);
      }
    } catch (err) {
      setError('Failed to load login requests');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!requestToDelete) return;

    try {
      setError('');
      await repAPI.deleteLeadLoginRequest(lead.id, requestToDelete.id);
      setDeleteConfirmOpen(false);
      setRequestToDelete(null);
      onRequestUpdated();
    } catch (err) {
      setError(err.message || 'Failed to delete login request');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'APPROVED': return 'success';
      case 'REJECTED': return 'error';
      default: return 'default';
    }
  };

  const formatRole = (role) => {
    return role
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const pendingRequests = loginRequests.filter(r => r.status === 'PENDING');
  const otherRequests = loginRequests.filter(r => r.status !== 'PENDING');

  return (
    <>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>
            Login Requests
          </Typography>
          {isRep && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setDialogOpen(true)}
            >
              Request Login
            </Button>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {loginRequests.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
              {isRep 
                ? 'No login requests yet. Request login access for contacts who need portal access.'
                : 'No login requests for this lead.'
              }
            </Typography>
          </Box>
        ) : (
          <>
            {pendingRequests.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>
                  Pending Approval
                </Typography>
                <List sx={{ p: 0, mb: 2 }}>
                  {pendingRequests.map((request, index) => (
                    <Fragment key={request.id}>
                      <ListItem
                        secondaryAction={
                          isRep && request.status === 'PENDING' ? (
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => {
                                setRequestToDelete(request);
                                setDeleteConfirmOpen(true);
                                setError('');
                              }}
                              title="Delete request"
                              sx={{ color: 'error.main' }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          ) : null
                        }
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="subtitle2" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>
                                {request.firstName} {request.lastName}
                              </Typography>
                              <Chip 
                                label={request.status} 
                                size="small" 
                                color={getStatusColor(request.status)}
                              />
                              <Chip 
                                label={formatRole(request.role)} 
                                size="small" 
                                variant="outlined"
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <Email fontSize="small" sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="body2" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                                  {request.email}
                                </Typography>
                              </Box>
                              {request.contact && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                                  Linked to contact: {request.contact.firstName} {request.contact.lastName}
                                </Typography>
                              )}
                              <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                {request.hasPortalAccess && (
                                  <Chip label="Portal Access" size="small" variant="outlined" />
                                )}
                                {request.canPlaceOrders && (
                                  <Chip label="Place Orders" size="small" variant="outlined" />
                                )}
                                {request.canPayInvoices && (
                                  <Chip label="Pay Invoices" size="small" variant="outlined" />
                                )}
                                {request.canViewReports && (
                                  <Chip label="View Reports" size="small" variant="outlined" />
                                )}
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < pendingRequests.length - 1 && <Divider />}
                    </Fragment>
                  ))}
                </List>
                {otherRequests.length > 0 && <Divider sx={{ my: 2 }} />}
              </>
            )}

            {otherRequests.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>
                  {pendingRequests.length > 0 ? 'Processed' : 'All Requests'}
                </Typography>
                <List sx={{ p: 0 }}>
                  {otherRequests.map((request, index) => (
                    <Fragment key={request.id}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="subtitle2" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>
                                {request.firstName} {request.lastName}
                              </Typography>
                              <Chip 
                                label={request.status} 
                                size="small" 
                                color={getStatusColor(request.status)}
                                icon={request.status === 'APPROVED' ? <CheckCircle /> : <Cancel />}
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Email fontSize="small" sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="body2" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                                  {request.email}
                                </Typography>
                              </Box>
                              {request.rejectedReason && (
                                <Typography variant="caption" color="error" sx={{ fontFamily: 'Poppins, sans-serif', mt: 0.5, display: 'block' }}>
                                  Rejection reason: {request.rejectedReason}
                                </Typography>
                              )}
                              {request.approvedAt && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif', mt: 0.5, display: 'block' }}>
                                  {request.status === 'APPROVED' ? 'Approved' : 'Rejected'} on {new Date(request.approvedAt).toLocaleDateString()}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < otherRequests.length - 1 && <Divider />}
                    </Fragment>
                  ))}
                </List>
              </>
            )}
          </>
        )}
      </Paper>

      {/* Request Login Dialog */}
      <LeadLoginRequestDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        lead={lead}
        contact={null}
        onSuccess={() => {
          setDialogOpen(false);
          onRequestUpdated();
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Login Request</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Typography sx={{ fontFamily: 'Poppins, sans-serif' }}>
            Are you sure you want to delete this login request? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LeadLoginRequests;

